import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FieldType } from '@ngx-formly/core';
import { catchError, map, of, startWith, switchMap, type Observable } from 'rxjs';

import { ModulesService } from '../../../api/services/modules.service';
import { composeEntriesFilter } from '../../../features/admin/modules/features/entries/entries-filter.util';
import { FormHelper } from '../../../features/admin/modules/features/form-builder/form.util';
import type { Entry } from '../../../features/admin/modules/modules.model';
import { ModulesStore } from '../../../features/admin/modules/modules.store';
import { debugLog } from '../../utils/debug-logger';
import { AutomaticChecksViewComponent } from './automatic-checks-view.component';
import type { AutomaticChecksPaths, RdwCheckState, RdwQuery } from './automatic-checks.types';
import {
  AUTOMATIC_CHECKS_DEFAULT_ASSETS_MODULE,
  buildAssetsFilter,
  buildKvkRow,
  buildRdwRow,
  resolveCheckPaths,
  summarizeRdwChecks,
} from './automatic-checks.util';
import { liveEntrySourceOf } from './readonly/display-context.util';

/** Upper bound of assets read per company; far above any real fleet on one company. */
const ASSETS_LIMIT = 1000;

/**
 * `automatic-checks`: read-only card with the KvK and RDW checks of a company entry.
 *
 * KvK comes from the entry's own values; RDW is fetched live from the assets that link to this
 * entry, because the counts are not stored on the company. Never writes its control.
 *
 * Reads the entry through `formState` (never `field.model` / the control), so the keyless clone the
 * Overzicht card mounts on the edit face renders the same thing (`CLONEABLE_OWN_VIEW_TYPES`).
 *
 * Always renders `bare`: on a detail page the Overzicht card or the tab section card already draws
 * the card and its heading, so a second one would nest a box in a box.
 * ponytail: no heading of its own, so it is meant to sit alone on a tab or in the Overzicht; placed
 * between other fields the rows are unlabeled. Add an opt-in title prop when a config needs that.
 *
 * Props (all optional): `assetsModuleCode` (default `assets`) and the config-only `checkPaths`
 * (partial `AutomaticChecksPaths` overrides).
 */
@Component({
  selector: 'formly-field-automatic-checks',
  imports: [AutomaticChecksViewComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `<dd-automatic-checks-view [bare]="true" [rows]="rows()" />`,
})
export class FormlyAutomaticChecksComponent extends FieldType {
  /**
   * The API client, not `EntriesService.getEntries`: that one caches `assets` queries (nothing
   * invalidates them when the monthly backend check writes new dates) and leaves its per-module
   * loading flag stuck after an HTTP error.
   */
  readonly #modulesService = inject(ModulesService);
  readonly #modulesStore = inject(ModulesStore);

  /** Live formState from the detail page when there is one; the static snapshot otherwise. */
  readonly #formState = computed(
    () => liveEntrySourceOf(this.options?.formState)?.formState() ?? this.options?.formState
  );
  readonly #entry = computed(() => this.#formState()?.entry as Entry | undefined);
  readonly #paths = computed(() =>
    resolveCheckPaths(this.props['checkPaths'] as Partial<AutomaticChecksPaths> | undefined)
  );

  /** Resolved so the KvK badge takes the status option's own colour (the one badge rule). */
  readonly #kvkStatusField = computed(() => {
    const moduleId = this.#formState()?.moduleId ?? this.#entry()?.moduleId;
    const generatedForm = moduleId ? this.#modulesStore.generatedForms()[moduleId] : undefined;
    return generatedForm
      ? FormHelper.getFieldByKeyPath(`entry.${this.#paths().kvkStatus}`, generatedForm)
      : undefined;
  });

  readonly #rdwQuery = computed<RdwQuery | null>(
    () => {
      const companyId = this.#entry()?.id;
      if (!companyId) {
        return null;
      }
      const code = (this.props['assetsModuleCode'] as string | undefined)?.trim() || AUTOMATIC_CHECKS_DEFAULT_ASSETS_MODULE;
      return {
        moduleId: this.#modulesStore.enabledModules().find(module => module.code === code)?.id,
        filter: buildAssetsFilter(companyId, this.#paths()),
      };
    },
    // formState recomputes on every keystroke; only a different company or module refetches.
    { equal: (a, b) => a?.moduleId === b?.moduleId && a?.filter === b?.filter }
  );

  readonly #rdwState = toSignal(toObservable(this.#rdwQuery).pipe(switchMap(query => this.#loadRdw(query))), {
    initialValue: { status: 'loading' } as RdwCheckState,
  });

  readonly rows = computed(() => {
    const now = new Date();
    return [
      buildKvkRow(this.#entry()?.entry, this.#paths(), now, this.#kvkStatusField(), this.#modulesStore),
      buildRdwRow(this.#rdwState(), now),
    ];
  });

  /** Last-check date of the latest successful fetch, shown next to a later fetch error. */
  #lastKnownCheck: string | undefined;

  #loadRdw(query: RdwQuery | null): Observable<RdwCheckState> {
    if (!query) {
      return of({ status: 'idle' });
    }
    if (!query.moduleId) {
      debugLog('AutomaticChecks', 'assets module not available', { code: this.props['assetsModuleCode'] });
      return of({ status: 'error' });
    }
    return this.#modulesService
      .getModuleEntries({
        moduleId: query.moduleId,
        // composeEntriesFilter adds the same archived base clause every entries list uses.
        $filter: composeEntriesFilter(query.filter),
        $top: String(ASSETS_LIMIT),
        $skip: '0',
      })
      .pipe(
        map((entries): RdwCheckState => {
          const summary = summarizeRdwChecks(
            entries.map(asset => asset.entry),
            new Date(),
            this.#paths()
          );
          this.#lastKnownCheck = summary.lastCheck;
          return { status: 'ready', summary };
        }),
        catchError((error: unknown) => {
          console.warn('[AutomaticChecks] RDW status could not be loaded', error);
          const forbidden = error instanceof HttpErrorResponse && error.status === 403;
          return of<RdwCheckState>({ status: 'error', forbidden, lastCheck: this.#lastKnownCheck });
        }),
        startWith<RdwCheckState>({ status: 'loading' })
      );
  }
}
